import axios from "axios";
import AppEnvVariablesService from '@core/services/utils/appEnvService';
import NavigationService from "@core/services/utils/appNavigationService";
import appConsoleService from '@core/services/utils/appConsoleService';
import {useLayoutStore} from '@core/stores/layoutStore';
import appNotificationService from '@core/services/utils/appNotificationService';
import {useAppEnvStore} from '@core/stores/app/appEnvStore';
import router from "src/router";
import {useAppMenuShortcutStore} from '@core/stores/app/appMenuShortcutStore';
import AppPresenceService from '@core/services/utils/appPresenceService';

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

// ───────────────────────────────────────────────────────────────────────────
// Disjoncteur de connexion (circuit breaker).
//
// Quand le backend tombe (reboot, réseau coupé), CHAQUE requête échoue et
// empilait un toast d'erreur → flot illisible. Ici on compte les échecs de
// CONNEXION consécutifs (pas de réponse, ou 502/503/504 = backend qui
// redémarre). Au-delà du seuil on bascule en « maintenance » : on route vers
// /no-landing (mode maintenance via appEnvStore.status='error') et on coupe les
// toasts. Un sondage périodique détecte le retour du backend et recharge l'app.
// Toute réponse du serveur (même une erreur applicative 4xx) prouve qu'il
// répond → on réarme le compteur (et on récupère si on était en panne).
const CONN_FAILURE_THRESHOLD = 5;     // nb d'échecs consécutifs avant maintenance
const CONN_POLL_MS = 5000;            // intervalle de sondage en maintenance
let connFailures = 0;
let maintenanceTripped = false;
let connPollTimer = null;

function isUnreachableError(error) {
  // Pas de réponse = backend injoignable ; 502/503/504 = passerelle/redémarrage.
  return !error || !error.response || [502, 503, 504].includes(error.response.status);
}

function recordReachable() {
  connFailures = 0;
  appNotificationService.setConnectivityDown(false);
  if (maintenanceTripped) recoverMaintenance();
}

function recordUnreachable() {
  if (maintenanceTripped) return;          // déjà en maintenance : on ignore le flot
  // Serveur injoignable : les erreurs poussées par les services (catch → pushError) sont
  // coupées à la source ; seul le 1er toast du disjoncteur passe (cf. shouldNotify).
  if (++connFailures > 1) appNotificationService.setConnectivityDown(true);
  if (connFailures >= CONN_FAILURE_THRESHOLD) tripMaintenance();
}

// Faut-il afficher le toast pour CETTE erreur ? Erreurs applicatives (4xx/500) :
// oui. Erreurs de connexion : seulement la 1re tentative, puis silence (et plus
// rien une fois en maintenance).
function shouldNotify(unreachable) {
  if (!unreachable) return true;
  if (maintenanceTripped) return false;
  return connFailures <= 1;
}

function tripMaintenance() {
  maintenanceTripped = true;
  // La fenêtre « Maintenance en cours / Réessayer » suffit : plus aucun toast d'erreur
  appNotificationService.setConnectivityDown(true);
  appNotificationService.dismissErrors();
  try { useAppEnvStore().setStatus('error'); } catch (e) { /* pinia pas prêt */ }
  NavigationService.goToMaintenance();
  startConnPoll();
}

function recoverMaintenance() {
  maintenanceTripped = false;
  connFailures = 0;
  stopConnPoll();
  // Backend revenu → reload complet pour re-bootstrapper proprement (env, menu…).
  if (typeof window !== 'undefined' && window.location) window.location.reload();
}

function startConnPoll() {
  if (connPollTimer) return;
  // Sonde légère : son SUCCÈS passe par l'intercepteur de réponse → recordReachable
  // → recoverMaintenance. Son échec est ignoré (maintenanceTripped).
  connPollTimer = setInterval(() => { service.get('/appEnv/load').catch(() => {}); }, CONN_POLL_MS);
}

function stopConnPoll() {
  if (connPollTimer) { clearInterval(connPollTimer); connPollTimer = null; }
}

// Écran courant (menuKey de la route) transmis au backend : le journal d'activité
// (AppActivityInterceptor) en déduit les consultations et rattache les actions à
// l'écran d'origine. Uniquement vers notre API (pas d'en-tête vers un tiers : il
// déclencherait un preflight CORS refusé).
function currentMenuKey() {
  try {
    return useAppMenuShortcutStore().menuKeyForRoute(router.currentRoute.value);
  } catch (e) {
    return null;                            // pinia / router pas encore prêts
  }
}

function isOwnApi(url) {
  const domain = AppEnvVariablesService.getEnv("APP_DOMAIN");
  return !!url && !!domain && url.includes(domain + "/api/");
}

axios.interceptors.request.use(request => {
  if (isOwnApi(request.url)) {
    const menuKey = currentMenuKey();
    if (menuKey) request.headers['X-App-Menu-Key'] = menuKey;
    // Présence réelle : secondes depuis la dernière interaction, seulement si l'application
    // est au premier plan. Sans cet en-tête (arrière-plan, géolocalisation, sonde), le
    // backend ne touche pas à la dernière connexion ni au statut en ligne.
    const idle = AppPresenceService.idleSeconds();
    if (idle !== null) request.headers['x-user-idle'] = String(idle);
  }
  return request;
}, error => {
  console.error('Request Error:', error);

  // Network error or backend not available
  if (!error.response) {
    recordUnreachable();
    if (shouldNotify(true)) {
      appNotificationService.pushError('Cannot connect to the server. Please check your network connection or try again later.');
    }
  }

  return Promise.reject(error);
});

// Handle response errors
axios.interceptors.response.use(response => {
  recordReachable();          // le backend répond → disjoncteur réarmé
  return response;
}, error => {
  // Signal de connectivité AVANT toute sortie anticipée : même les sondes
  // silencieuses contribuent à détecter un backend injoignable.
  const unreachable = isUnreachableError(error);
  if (unreachable) recordUnreachable(); else recordReachable();
  // Some endpoints handle their own 401 silently (login attempts, silent auto-login
  // probes). Skip the global handler for those and let the caller deal with the
  // rejection — otherwise the user is bounced to /401 on every public-page visit.
  const url = (error.config && error.config.url) || '';
  // Endpoints à fallback silencieux (sondes / tentatives optionnelles) : pas de
  // notification globale, le caller gère la suite (ex. passkey absent → magic-link).
  const isSilentProbe = url.includes('api/login')
                   || url.includes('/sosCandidateAuth/tryAutoLogin')
                   || url.includes('/sosCandidateAuth/webauthnAuthStart')
                   || url.includes('/appPasskey/status')
                   || url.includes('/appPasskey/loginOptions')
                   || url.includes('/appPasskey/login');
  if (isSilentProbe) {
    return Promise.reject(error);
  }

  // Détail technique pour le debug (jamais montré tel quel à l'utilisateur).
  appConsoleService.error('AxiosService', error.message);

  // Codes d'erreur (429) gérés EN CONTEXTE par l'appelant — ex. quota/génération
  // déjà en cours du dialog de génération candidat, qui affiche déjà un bandeau
  // dédié. Pas de toast global redondant pour ceux-là.
  const callerHandledCodes = ['GEN_QUOTA_EXCEEDED', 'GEN_IN_PROGRESS'];
  const handledByCaller = callerHandledCodes.includes(error.response?.data?.code);

  if (error.response && error.response.status === 401) {
    // Session expirée → redirection vers la page dédiée (pas de toast).
    (async () => {
      await NavigationService.goTo401();
    })();
  } else if (!handledByCaller && shouldNotify(unreachable)) {
    // Réseau, 404, 5xx, validation… : message humanisé et rassurant,
    // dédoublonné côté appNotificationService (intercepteur + catch service).
    // En cas de perte de connexion, shouldNotify coupe le flot (1 toast max,
    // puis silence + bascule maintenance au seuil).
    appNotificationService.pushError(error);
  }

  console.error('Response Error:', JSON.stringify({
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data
  }, null, 2));

  return Promise.reject(error);
});

class AxiosService {

  /**
   * Get function
   * @param target
   * @param headers
   * @returns {Promise<AxiosResponse<any>>}
   */
  async get(target, config = {}) {
    try {

      const APP_PROTOCOL = AppEnvVariablesService.getEnv("APP_PROTOCOL")
      const APP_DOMAIN = AppEnvVariablesService.getEnv("APP_DOMAIN");
      const ENDPOINT = APP_PROTOCOL + "://" + APP_DOMAIN + "/api";

      Object.assign(config, {cancelToken: source.token})
      appConsoleService.info('AxiosService', 'GET :' + target)

      let layoutStore = useLayoutStore()
      let lang = layoutStore.language
      return await axios.get(ENDPOINT + target , config);

    } catch (error) {
      // Sans réponse (serveur injoignable) : on relance l'erreur réseau d'origine (pas de TypeError)
      if (error.response?.status !== 401) {
        throw error;
      }
    }
  }

  async getFrom(target, config = {}) {
    try {

      Object.assign(config, {cancelToken: source.token})
      return await axios.get(target , config);

    } catch (error) {
      // Sans réponse (serveur injoignable) : on relance l'erreur réseau d'origine (pas de TypeError)
      if (error.response?.status !== 401) {
        throw error;
      }
    }
  }

  async postTo(target, data, config = {}) {
    try {

      Object.assign(config, {cancelToken: source.token})
      return await axios.post(target , data, config);

    } catch (error) {
      // Sans réponse (serveur injoignable) : on relance l'erreur réseau d'origine (pas de TypeError)
      if (error.response?.status !== 401) {
        throw error;
      }
    }
  }

  /**
   * Post function
   * @param target
   * @param payload
   * @param headers
   * @returns {Promise<AxiosResponse<any>>}
   */
  async post(target, payload, config = {}) {
    try {

      const APP_PROTOCOL = AppEnvVariablesService.getEnv("APP_PROTOCOL")
      const APP_DOMAIN = AppEnvVariablesService.getEnv("APP_DOMAIN");
      const ENDPOINT = APP_PROTOCOL + "://" + APP_DOMAIN + "/api";

      Object.assign(config, {cancelToken: source.token})
      appConsoleService.info('AxiosService', 'POST:' + target)

      let layoutStore = useLayoutStore()
      let lang = layoutStore.language
      return await axios.post(ENDPOINT + target, payload, config);

    } catch (error) {
      // Sans réponse (serveur injoignable) : on relance l'erreur réseau d'origine (pas de TypeError)
      if (error.response?.status !== 401) {
        throw error;
      }
    }
  }

  /**
   * Cancel all pending requests
   * @returns {Promise<void>}
   */
  cancel() {
    source.cancel('Operation canceled by the user.');
  }

}

// Singleton nommé : référencé par le sondage du disjoncteur (startConnPoll).
const service = new AxiosService();
export default service;
